import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import ElectricBolt from "@mui/icons-material/ElectricBolt";
import { Divider, IconButton, Popover, Tooltip } from "@mui/material";
import { type FC, type MouseEvent, useCallback, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import type { ChannelName } from "../../../../channels/types";
import type { MomentFormMoment } from "../../../../moment/types/moment-form-moment";
import { useLexicalContext } from "../../lexical-context";
import { floatingToolbarStyles } from "../toolbar/floating-toolbar-styles";
import { CoPilot } from "./co-pilot";
import { useCoPilot } from "./use-co-pilot";
import { useLexicalSelection } from "./use-lexical-selection";
import { insert, replaceSelection } from "./utils";

export const CoPilotToolbarButton: FC<{
  channel: NonNullable<ChannelName>;
  format: "email" | "chat" | "sms";
  disabled?: boolean;
}> = ({ channel, format, disabled }) => {
  const { filterTags } = useLexicalContext();
  const { control } = useFormContext<MomentFormMoment>();
  useWatch({ control, name: ["id"] });
  const [editor] = useLexicalComposerContext();
  const [open, setOpen] = useState(false);
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | undefined>();

  const mergeTags = useMemo(
    () =>
      Object.values(filterTags(""))
        .flatMap((x) => x.groupTags)
        .map((x) => `{{${x.id}}}`),
    [filterTags],
  );

  const selection = useLexicalSelection();
  // Snapshot of selection captured at click time — Lexical clears selection when
  // the button receives focus, so we store it in state before the popover opens.
  const [capturedSelection, setCapturedSelection] = useState(selection);

  const coPilot = useCoPilot({
    channel,
    mergeTags,
    tone: "professional",
    selection,
  });

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      setCapturedSelection(selection);
      // Capture position at click time — the floating toolbar unmounts when Lexical
      // clears selection on focus change, so anchorEl becomes null before MUI can
      // use it. anchorReference="anchorPosition" sidesteps the stale ref.
      const rect = e.currentTarget.getBoundingClientRect();
      setAnchorPosition({ top: rect.bottom, left: rect.left });
      setOpen(true);
    },
    [selection],
  );

  const handleClose = useCallback(() => setOpen(false), []);

  const handleReplace = useCallback(
    (text: string) => {
      replaceSelection(editor, format, text);
      setOpen(false);
    },
    [editor, format],
  );

  const handleInsert = useCallback(
    (markdown: string) => {
      insert(editor, format, markdown);
      setOpen(false);
    },
    [editor, format],
  );

  return (
    <>
      <Divider orientation="vertical" flexItem />
      <Tooltip title="Co-pilot">
        <IconButton
          data-analytics-id="lexical-co-pilot-floating-toolbar-button"
          tabIndex={-1}
          disabled={disabled}
          onClick={handleClick}
          sx={floatingToolbarStyles["floating"].button}
        >
          <ElectricBolt sx={floatingToolbarStyles["floating"].formatIcon} />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition}
        onClose={handleClose}
        disableScrollLock={true}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <CoPilot
          coPilot={coPilot}
          selection={capturedSelection}
          onInsert={handleInsert}
          onReplace={handleReplace}
          maxHeight="850px"
          width="max(500px, 30vw)"
          m={1}
        />
      </Popover>
    </>
  );
};
